import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Input } from './ui/input';
import { MapPin, Search, Loader2 } from 'lucide-react';
import { addressLookup } from '../services/api';

/**
 * Reusable UK-address input with two combined search strategies:
 *
 *   1. `existingCustomers` — a list of `{name, address}` objects
 *      passed in by the parent. Filtered client-side by the current
 *      input value. Selecting one calls `onCustomerPick` (if provided)
 *      OR `onChange` with the address string.
 *
 *   2. **Postcode / partial-address lookup** — after 250 ms of idle
 *      typing (and at least 3 characters), calls the backend
 *      `/api/address/lookup` proxy (Nominatim / OSM). Results appear
 *      under the customer list.
 *
 * Props:
 *   value               current input value (string) — controlled
 *   onChange(str)       fires on every keystroke
 *   onAddressPicked(a)  fires when a lookup result is picked. Payload
 *                       is the flattened Nominatim hit. Falls back to
 *                       onChange(oneLine) if not provided.
 *   existingCustomers   optional [{id, name, address, ...}] to
 *                       autocomplete against. Match runs on both
 *                       name and address.
 *   onCustomerPick(c)   optional — fires when an existing customer is
 *                       picked. Parent decides how to fill the form.
 *   placeholder / disabled / testId — passthrough.
 */
const AddressLookup = ({
  value,
  onChange,
  onAddressPicked,
  existingCustomers = [],
  onCustomerPick,
  placeholder = 'Type customer name, postcode or address…',
  disabled = false,
  testId = 'address-lookup',
}) => {
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [highlight, setHighlight] = useState(-1);
  const anchorRef = useRef(null);
  const popRef = useRef(null);
  const debounceRef = useRef(null);

  // Filter existing customers client-side.
  const customerMatches = useMemo(() => {
    const q = (value || '').trim().toLowerCase();
    if (!q || q.length < 2) return [];
    return existingCustomers
      .filter((c) => {
        const hay = `${c.company || ''} ${c.name || ''} ${c.address || ''}`.toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 6);
  }, [value, existingCustomers]);

  useEffect(() => {
    // Debounced postcode / OSM lookup.
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = (value || '').trim();
    if (q.length < 3) {
      setResults([]);
      return undefined;
    }
    debounceRef.current = setTimeout(() => {
      setLoading(true);
      setError('');
      addressLookup(q)
        .then((r) => setResults(r?.results || []))
        .catch((e) => setError(e?.response?.data?.detail || 'Lookup unavailable'))
        .finally(() => setLoading(false));
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [value]);

  useEffect(() => {
    // Click-away to close the dropdown.
    if (!open) return undefined;
    const clickAway = (e) => {
      if (
        anchorRef.current && !anchorRef.current.contains(e.target)
        && popRef.current && !popRef.current.contains(e.target)
      ) setOpen(false);
    };
    document.addEventListener('mousedown', clickAway);
    return () => document.removeEventListener('mousedown', clickAway);
  }, [open]);

  const totalItems = customerMatches.length + results.length;

  const pick = (item, kind) => {
    if (kind === 'customer') {
      if (onCustomerPick) onCustomerPick(item);
      else onChange(item.address || item.company || item.name || '');
    } else if (kind === 'address') {
      if (onAddressPicked) onAddressPicked(item);
      else onChange(item.oneLine || item.formatted || '');
    }
    setOpen(false);
    setHighlight(-1);
  };

  const onKeyDown = (e) => {
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(totalItems - 1, h + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(-1, h - 1));
    } else if (e.key === 'Enter' && highlight >= 0) {
      e.preventDefault();
      if (highlight < customerMatches.length) {
        pick(customerMatches[highlight], 'customer');
      } else {
        pick(results[highlight - customerMatches.length], 'address');
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const showEmptyHint = open && (value || '').trim().length >= 2
    && !loading && customerMatches.length === 0 && results.length === 0;

  return (
    <div className="relative" ref={anchorRef}>
      <Input
        value={value || ''}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        data-testid={testId}
        className="pr-9"
      />
      {loading ? (
        <Loader2 className="absolute right-2.5 top-2.5 h-4 w-4 text-slate-400 animate-spin" />
      ) : (
        <Search className="absolute right-2.5 top-2.5 h-4 w-4 text-slate-400 pointer-events-none" />
      )}
      {open && (customerMatches.length > 0 || results.length > 0 || loading || showEmptyHint || error) && (
        <div
          ref={popRef}
          className="absolute z-[1001] mt-1 left-0 right-0 bg-white border border-slate-200 rounded-md shadow-lg max-h-80 overflow-auto"
          data-testid={`${testId}-popover`}
        >
          {customerMatches.length > 0 && (
            <div>
              <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold bg-slate-50 border-b border-slate-100">
                Existing customers
              </div>
              {customerMatches.map((c, i) => (
                <button
                  key={c.id || c.company || c.name}
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); pick(c, 'customer'); }}
                  onMouseEnter={() => setHighlight(i)}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 border-b border-slate-100 last:border-b-0 ${highlight === i ? 'bg-slate-50' : ''}`}
                  data-testid={`${testId}-customer-${c.id || i}`}
                >
                  <div className="font-medium text-slate-900 truncate">{c.company || c.name}</div>
                  {c.address && (
                    <div className="text-[11px] text-slate-500 truncate">{c.address}</div>
                  )}
                </button>
              ))}
            </div>
          )}
          {(loading || results.length > 0) && (
            <div>
              <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold bg-slate-50 border-b border-slate-100 flex items-center gap-1.5">
                <MapPin className="h-3 w-3" />
                UK address lookup {loading && '· searching…'}
              </div>
              {results.map((a, i) => {
                const globalIdx = customerMatches.length + i;
                return (
                  <button
                    key={`${a.formatted}-${i}`}
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); pick(a, 'address'); }}
                    onMouseEnter={() => setHighlight(globalIdx)}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 border-b border-slate-100 last:border-b-0 ${highlight === globalIdx ? 'bg-slate-50' : ''}`}
                    data-testid={`${testId}-address-${i}`}
                  >
                    <div className="font-medium text-slate-900 truncate">{a.oneLine || a.formatted}</div>
                    <div className="text-[11px] text-slate-500 truncate">{a.formatted}</div>
                  </button>
                );
              })}
            </div>
          )}
          {showEmptyHint && !error && (
            <div className="px-3 py-3 text-xs text-slate-500 italic">
              No matches. Keep typing — postcodes work too (e.g. <span className="font-mono">PR9 9AL</span>).
            </div>
          )}
          {error && (
            <div className="px-3 py-2 text-[11px] text-amber-800 bg-amber-50 border-t border-amber-100">
              {error}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AddressLookup;
